// Custom Query Hooks for Clayworks Booking
// /app/frontend/src/hooks/useQueries.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { queryKeys, invalidateQueries } from '../lib/queryClient';
import { toast } from 'sonner';

// ======================= VENUES =======================

export function useVenues(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.venues.list(),
    queryFn: async () => {
      const { data } = await api.get('/venues');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useAllVenues(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.venues.all,
    queryFn: async () => {
      const { data } = await api.get('/venues/all');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useVenue(venueId, options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.venues.detail(venueId),
    queryFn: async () => {
      const { data } = await api.get(`/venues/${venueId}`);
      return data;
    },
    enabled: !!venueId,
    ...options,
  });
}

// ======================= BOOKABLES =======================

export function useBookables(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookables.list(),
    queryFn: async () => {
      const { data } = await api.get('/bookables');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useAllBookables(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookables.all,
    queryFn: async () => {
      const { data } = await api.get('/bookables/all');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useBookablesByVenue(venueId, options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookables.byVenue(venueId),
    queryFn: async () => {
      const { data } = await api.get(`/bookables?venue_id=${venueId}`);
      return data;
    },
    enabled: !!venueId,
    ...options,
  });
}

export function useAvailability(bookableId, date, options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookables.availability(bookableId, date),
    queryFn: async () => {
      const { data } = await api.get(`/bookables/${bookableId}/availability?date=${date}`);
      return data;
    },
    enabled: !!bookableId && !!date,
    staleTime: 1 * 60 * 1000, // 1 minute for availability (needs to be fresh)
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    ...options,
  });
}

// ======================= BOOKINGS =======================

export function useBookings(filters = {}, options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookings.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.venue_id) params.append('venue_id', filters.venue_id);
      if (filters.date) params.append('date', filters.date);
      
      const { data } = await api.get(`/bookings?${params.toString()}`);
      return data;
    },
    ...options,
  });
}

export function useBooking(bookingId, options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.bookings.detail(bookingId),
    queryFn: async () => {
      const { data } = await api.get(`/bookings/${bookingId}`);
      return data;
    },
    enabled: !!bookingId,
    ...options,
  });
}

export function useCreateBooking() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (bookingData) => {
      const { data } = await api.post('/bookings', bookingData);
      return data;
    },
    onSuccess: () => {
      toast.success('Booking created successfully!');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to create booking';
      toast.error(message);
    },
  });
}

export function useCancelBooking() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async (bookingId) => {
      const { data } = await api.post(`/bookings/${bookingId}/cancel`);
      return data;
    },
    onSuccess: () => {
      toast.success('Booking cancelled successfully');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to cancel booking';
      toast.error(message);
    },
  });
}

export function useRescheduleBooking() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async ({ bookingId, newDate, newStartTime, newEndTime }) => {
      const { data } = await api.post(`/bookings/${bookingId}/reschedule`, {
        new_booking_date: newDate,
        new_start_time: newStartTime,
        new_end_time: newEndTime,
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Booking rescheduled successfully');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to reschedule booking';
      toast.error(message);
    },
  });
}

// ======================= HOLIDAYS =======================

export function useHolidays(options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.holidays.list(),
    queryFn: async () => {
      const { data } = await api.get('/holidays');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// ======================= CLIENT LEADS (Client Database) =======================

export function useClientLeads(options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.clientLeads.list(),
    queryFn: async () => {
      const { data } = await api.get('/client-leads');
      return data;
    },
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000, // shared list — poll so a teammate's "called" mark shows up
    ...options,
  });
}

export function useTriggerLeadCall() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (leadId) => {
      const { data } = await api.post(`/client-leads/${leadId}/call`);
      return data;
    },
    onSuccess: (data) => {
      // Native dialer handoff: the customer only sees the rep's real mobile
      // number as caller ID if the rep's own phone places the call, so we
      // open tel: here (backend just logs the attempt, it can't dial).
      if (data?.phone) {
        window.location.href = `tel:${data.phone}`;
      }
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to start the call');
    },
  });
}

// Admin/Hub Manager only — total + per-rep call counts for the Client
// Database's Call Activity panel, scoped to { from_date, to_date } (YYYY-MM-DD,
// both inclusive). Polls so counts placed by other reps show up without a
// manual refresh, and refetches automatically whenever the date range changes
// since that range is part of the query key.
export function useCallStats({ from_date, to_date } = {}, options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.clientLeads.callStats({ from_date, to_date }),
    queryFn: async () => {
      const { data } = await api.get('/client-leads/call-stats', { params: { from_date, to_date } });
      return data;
    },
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    ...options,
  });
}

// Admin/Hub Manager only — paginated call_logs for a period (+ optional
// sales_user_id filter), the drill-down behind the Call Activity summary.
export function useCallHistory({ from_date, to_date, sales_user_id, page = 1, page_size = 20 } = {}, options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.clientLeads.callHistory({ from_date, to_date, sales_user_id, page, page_size }),
    queryFn: async () => {
      const { data } = await api.get('/client-leads/call-history', {
        params: { from_date, to_date, sales_user_id, page, page_size },
      });
      return data;
    },
    ...options,
  });
}

export function useLeadCallLogs(leadId, options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.clientLeads.callLogs(leadId),
    queryFn: async () => {
      const { data } = await api.get(`/client-leads/${leadId}/call-logs`);
      return data;
    },
    enabled: !!leadId,
    ...options,
  });
}

export function useUpdateCallLogNote() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, callLogId, outcome, notes }) => {
      const { data } = await api.patch(`/client-leads/${leadId}/call-logs/${callLogId}`, { outcome, notes });
      return data;
    },
    onSuccess: () => {
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to save the call note');
    },
  });
}

export function useUploadBatches(options = {}) {
  const { api } = useAuth();

  return useQuery({
    queryKey: queryKeys.clientLeads.uploadBatches(),
    queryFn: async () => {
      const { data } = await api.get('/client-leads/upload-batches');
      return data;
    },
    ...options,
  });
}

export function useUpdateBatchAccess() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async ({ batchId, salesUserIds }) => {
      const { data } = await api.patch(`/client-leads/upload-batches/${batchId}/access`, { sales_user_ids: salesUserIds });
      return data;
    },
    onSuccess: () => {
      toast.success('Access updated');
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    },
  });
}

export function useDeleteBatch() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (batchId) => {
      const { data } = await api.delete(`/client-leads/upload-batches/${batchId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Uploaded file removed');
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete the uploaded file');
    },
  });
}

export function useUploadClientLeads() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/client-leads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to upload client leads');
    },
  });
}

export function useMarkLeadCalled() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, notes }) => {
      const { data } = await api.post(`/client-leads/${leadId}/mark-called`, { notes });
      return data;
    },
    onSuccess: () => {
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to mark as called');
    },
  });
}

export function useUnmarkLeadCalled() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (leadId) => {
      const { data } = await api.post(`/client-leads/${leadId}/unmark-called`);
      return data;
    },
    onSuccess: () => {
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to unmark as called');
    },
  });
}

export function useUpdateClientLead() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, updates }) => {
      const { data } = await api.patch(`/client-leads/${leadId}`, updates);
      return data;
    },
    onSuccess: () => {
      toast.success('Client lead updated');
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update client lead');
    },
  });
}

export function useDeleteClientLead() {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (leadId) => {
      const { data } = await api.delete(`/client-leads/${leadId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Client lead removed');
      invalidateQueries.clientLeads();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to remove client lead');
    },
  });
}

// ======================= USERS & ORGANIZATIONS =======================

export function useUsers(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: async () => {
      const { data } = await api.get('/users');
      return data;
    },
    ...options,
  });
}

export function useOrganizations(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.organizations.list(),
    queryFn: async () => {
      const { data } = await api.get('/organizations');
      return data;
    },
    ...options,
  });
}

// ======================= CREDIT WALLETS =======================

export function useCreditWallets(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.creditWallets.list(),
    queryFn: async () => {
      const { data } = await api.get('/credit-wallets');
      return data;
    },
    ...options,
  });
}

export function useMyWallet(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.creditWallets.userWallet('me'),
    queryFn: async () => {
      const { data } = await api.get('/credit-wallets/my-wallet');
      return data;
    },
    ...options,
  });
}

// ======================= ANALYTICS =======================

export function useAnalyticsDashboard(period = 'month', options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.analytics.dashboard(period),
    queryFn: async () => {
      const { data } = await api.get(`/analytics/dashboard?period=${period}`);
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

export function useFoundersOverview(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.analytics.founders(),
    queryFn: async () => {
      const { data } = await api.get('/founders/overview');
      return data;
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useFoundersRealtime(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: [...queryKeys.analytics.founders(), 'realtime'],
    queryFn: async () => {
      const { data } = await api.get('/founders/real-time');
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000,
    ...options,
  });
}

export function useFinanceReport(reportType, period = 'month', options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.analytics.finance(reportType, period),
    queryFn: async () => {
      const { data } = await api.get(`/finance/${reportType}?period=${period}`);
      return data;
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

// ======================= CENTER MANAGER =======================

export function useCenterManagerStats(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.centerManager.stats(),
    queryFn: async () => {
      const { data } = await api.get('/center-manager/stats');
      return data;
    },
    staleTime: 1 * 60 * 1000,
    ...options,
  });
}

export function useTodaySchedule(options = {}) {
  const { api } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.centerManager.todaySchedule(),
    queryFn: async () => {
      const { data } = await api.get('/center-manager/today-schedule');
      return data;
    },
    staleTime: 1 * 60 * 1000,
    refetchInterval: 60 * 1000, // Refetch every minute
    ...options,
  });
}

export function useCheckIn() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async (bookingId) => {
      const { data } = await api.post(`/center-manager/check-in/${bookingId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Check-in successful');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to check in';
      toast.error(message);
    },
  });
}

export function useCheckOut() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async (bookingId) => {
      const { data } = await api.post(`/center-manager/check-out/${bookingId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Check-out successful');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to check out';
      toast.error(message);
    },
  });
}

export function useCreateWalkIn() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async (walkInData) => {
      const { data } = await api.post('/center-manager/walk-in', walkInData);
      return data;
    },
    onSuccess: () => {
      toast.success('Walk-in booking created');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to create walk-in';
      toast.error(message);
    },
  });
}

export function useBlockSlot() {
  const { api } = useAuth();
  
  return useMutation({
    mutationFn: async (blockData) => {
      const { data } = await api.post('/center-manager/block-slot', blockData);
      return data;
    },
    onSuccess: () => {
      toast.success('Slot blocked successfully');
      invalidateQueries.bookingRelated();
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Failed to block slot';
      toast.error(message);
    },
  });
}
